import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { persistAuth } from '../../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminLogin = () => {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data?.message || 'Invalid admin credentials');
        return;
      }

      // Store admin token separately from user token, and mirror into the
      // standard auth store so role-aware UI (RoleGuard / hasRole) works.
      localStorage.setItem('adminToken', data.token);
      persistAuth(data.token, { fullName: data.fullName, email: data.email });
      navigate('/admin/dashboard');

    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');

        .al-page {
          min-height: 100vh;
          background: #050508;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'DM Sans', sans-serif;
          padding: 24px;
          position: relative;
          overflow: hidden;
        }

        /* Background orbs */
        .al-orb-1 {
          position: absolute;
          width: 500px; height: 500px;
          top: -150px; left: -150px;
          background: radial-gradient(circle, rgba(0,201,167,0.07) 0%, transparent 65%);
          border-radius: 50%;
          pointer-events: none;
        }
        .al-orb-2 {
          position: absolute;
          width: 400px; height: 400px;
          bottom: -100px; right: -100px;
          background: radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 65%);
          border-radius: 50%;
          pointer-events: none;
        }
        .al-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent);
          pointer-events: none;
        }

        /* Card */
        .al-card {
          position: relative;
          z-index: 1;
          background: #0D0D12;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 48px 40px;
          width: 100%;
          max-width: 420px;
          text-align: center;
        }

        .al-shield {
          font-size: 44px;
          margin-bottom: 16px;
          display: block;
        }

        .al-title {
          font-family: 'Syne', sans-serif;
          font-size: 28px;
          font-weight: 800;
          color: #F0EEF8;
          margin: 0 0 8px;
          letter-spacing: -0.03em;
        }

        .al-sub {
          color: #6E6B80;
          font-size: 14px;
          margin: 0 0 36px;
        }

        /* Form */
        .al-form {
          text-align: left;
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .al-field {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }

        .al-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #6E6B80;
        }

        .al-input-wrap {
          position: relative;
        }

        .al-input {
          width: 100%;
          background: #111118;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 12px;
          padding: 13px 16px;
          color: #F0EEF8;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }
        .al-input:focus {
          border-color: rgba(0,255,209,0.4);
        }

        .al-eye {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #4A4760;
          font-size: 16px;
          padding: 0;
          transition: color 0.2s;
        }
        .al-eye:hover { color: #00FFD1; }

        /* Error */
        .al-error {
          background: rgba(248,113,113,0.08);
          border: 1px solid rgba(248,113,113,0.2);
          border-radius: 10px;
          padding: 12px 16px;
          color: #f87171;
          font-size: 13px;
          font-weight: 500;
          text-align: center;
        }

        /* Button */
        .al-btn {
          background: linear-gradient(135deg, #00C9A7, #00FFD1);
          color: #050508;
          border: none;
          border-radius: 12px;
          padding: 14px;
          font-size: 15px;
          font-weight: 700;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          width: 100%;
          margin-top: 4px;
          transition: opacity 0.2s, transform 0.15s;
          letter-spacing: 0.01em;
        }
        .al-btn:hover:not(:disabled) {
          opacity: 0.92;
          transform: translateY(-1px);
        }
        .al-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Spinner */
        .al-spinner {
          display: inline-block;
          width: 16px; height: 16px;
          border: 2px solid rgba(5,5,8,0.3);
          border-top-color: #050508;
          border-radius: 50%;
          animation: al-spin 0.7s linear infinite;
          vertical-align: middle;
          margin-right: 8px;
        }
        @keyframes al-spin { to { transform: rotate(360deg); } }

        /* Badge */
        .al-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 5px 12px;
          border-radius: 100px;
          background: rgba(0,201,167,0.1);
          border: 1px solid rgba(0,201,167,0.25);
          color: #00FFD1;
          margin-bottom: 20px;
        }
      `}</style>

      <div className="al-page">
        <div className="al-orb-1" />
        <div className="al-orb-2" />
        <div className="al-grid"  />

        <div className="al-card">
          <span className="al-shield">🛡️</span>
          <div className="al-badge">✦ Admin Access Only</div>
          <h1 className="al-title">Admin Portal</h1>
          <p className="al-sub">Sign in with your admin credentials to continue</p>

          <form className="al-form" onSubmit={handleLogin}>

            <div className="al-field">
              <label className="al-label">Email</label>
              <div className="al-input-wrap">
                <input
                  className="al-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@campustojob.com"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="al-field">
              <label className="al-label">Password</label>
              <div className="al-input-wrap">
                <input
                  className="al-input"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  style={{ paddingRight: 44 }}
                  required
                />
                <button
                  type="button"
                  className="al-eye"
                  onClick={() => setShowPass(p => !p)}
                  tabIndex={-1}
                >
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && (
              <div className="al-error">⚠️ {error}</div>
            )}

            <button className="al-btn" type="submit" disabled={loading}>
              {loading
                ? <><span className="al-spinner" />Signing in…</>
                : 'Sign In →'
              }
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;